import { useEffect, useState, useMemo } from "react";
import { useThreadHistory } from "@/hooks";
import { useAuthStore, useThreadStore } from "@/store";
import { supabase } from "@/lib/supabase";
import { isDefaultTitle } from "@/features/sidebar/projects/utils/projectHelpers";
import { useThreadNewTracking } from "@/features/sidebar/projects/hooks/useThreadNewTracking";
import { filterHistoryThreads } from "../utils/historyGrouping";

/**
 * Hook for managing history threads data and real-time subscriptions
 */
export function useHistoryThreads() {
  const { threads = [] } = useThreadHistory();
  const { workspaces = [] } = useAuthStore();
  const { markThreadAsNew } = useThreadNewTracking();
  const [forceRefresh, setForceRefresh] = useState(0);

  // Real-time subscription for thread metadata updates
  useEffect(() => {
    const channel = supabase
      .channel(`thread-metadata-updates-${Date.now()}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "threads",
          filter: `user_id=eq.${useAuthStore.getState().user?.id}`,
        },
        (payload) => {
          const { threads: currentThreads, setThreads } =
            useThreadStore.getState();
          const updatedThread = payload.new;

          const wasInHistory = currentThreads.find(
            (t) => t.id === updatedThread.id
          )?.metadata?.moved_to_history_at;
          const isNowInHistory = updatedThread.metadata?.moved_to_history_at;

          if (
            !wasInHistory &&
            isNowInHistory &&
            !isDefaultTitle(updatedThread.title)
          ) {
            markThreadAsNew(
              updatedThread.id,
              updatedThread.title ||
                `Chat ${updatedThread.id.substring(0, 6)}...`
            );
          }

          const updatedThreads = currentThreads.map((t) =>
            t.id === updatedThread.id
              ? {
                  ...t,
                  metadata: updatedThread.metadata,
                  updated_at: updatedThread.updated_at,
                }
              : t
          );

          setThreads(updatedThreads);
          setForceRefresh((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "threads",
          filter: `user_id=eq.${useAuthStore.getState().user?.id}`,
        },
        (payload) => {
          const newThread = payload.new;

          if (!newThread.is_team && !isDefaultTitle(newThread.title)) {
            markThreadAsNew(
              newThread.id,
              newThread.title || `Chat ${newThread.id.substring(0, 6)}...`
            );
          }

          const { threads: currentThreads, setThreads } =
            useThreadStore.getState();
          setThreads([newThread, ...currentThreads]);

          setForceRefresh((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "threads",
          filter: `user_id=eq.${useAuthStore.getState().user?.id}`,
        },
        (payload) => {
          const deletedThreadId = payload.old?.id;
          if (deletedThreadId) {
            console.log(
              `[useHistoryThreads] Thread ${deletedThreadId} deleted, removing from store`
            );
            const { threads: currentThreads, setThreads } =
              useThreadStore.getState();
            const updatedThreads = currentThreads.filter(
              (t) => t.id !== deletedThreadId
            );
            setThreads(updatedThreads);
            setForceRefresh((prev) => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [markThreadAsNew]);

  // Filter and sort threads by display_order (fallback to updated_at for backward compatibility)
  const filteredAndSortedThreads = useMemo(() => {
    const filtered = filterHistoryThreads(threads, workspaces);

    // Sort by display_order first, then by updated_at as fallback
    return [...filtered].sort((a, b) => {
      // If both have display_order, use that
      if (a.display_order != null && b.display_order != null) {
        return a.display_order - b.display_order;
      }

      // If only one has display_order, prioritize it
      if (a.display_order != null) return -1;
      if (b.display_order != null) return 1;

      // Fallback to updated_at (most recent first)
      const dateA = new Date(a?.updated_at || a?.lastMessageAt || 0);
      const dateB = new Date(b?.updated_at || b?.lastMessageAt || 0);
      return dateB - dateA;
    });
  }, [threads, workspaces, forceRefresh]);

  return {
    threads: filteredAndSortedThreads,
    historyGroups: [], // Keep for backward compatibility but return empty
    forceRefresh,
  };
}
